import { type ButtonHTMLAttributes, type ReactNode } from 'react';

import clsx from 'clsx';

import { LoadingSpinner } from '../loading-spinner';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loadingText?: string;
  children: ReactNode;
  fullWidth?: boolean;
}

const variantClasses = {
  primary:
    'bg-violet-600 hover:bg-violet-700 text-white border border-violet-600 hover:border-violet-700',
  secondary:
    'bg-transparent hover:bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-600 hover:border-zinc-500',
  danger:
    'bg-transparent hover:bg-red-500/10 text-red-400 hover:text-red-300 border border-red-500/50 hover:border-red-500/70',
  ghost: 'bg-transparent hover:bg-zinc-700 text-zinc-300 hover:text-white border-none',
};

const sizeClasses = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2.5 text-sm',
  lg: 'px-6 py-3 text-base',
};

export const Button = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  loadingText,
  children,
  fullWidth = false,
  className = '',
  disabled,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || isLoading;

  return (
    <button
      className={clsx(
        'font-medium rounded-lg transition-all duration-200 inline-flex items-center justify-center gap-1',
        'focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 focus:ring-offset-zinc-900',
        'cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed',
        variantClasses[variant],
        sizeClasses[size],
        fullWidth && 'w-full',
        className,
      )}
      disabled={isDisabled}
      {...props}
    >
      {isLoading && <LoadingSpinner size={size === 'lg' ? 'md' : 'sm'} color="white" />}
      {isLoading && loadingText ? <span className="opacity-75">{loadingText}</span> : children}
    </button>
  );
};
